/* eslint-disable no-alert */
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { onChildAdded, ref, db, query, get } from "../../../firebase";
import { useInstance, useNetwork, useWebAuthn } from "hooks";
import {
  IsSignInTermAndConditionEnabledState,
  userLoginCredRespState,
  SignInFormState,
  SignInScreenState,
  IsVerifiedUserState,
  SigninLoadingState,
  BrowserRegistrationModalState,
  BrowserRegistrationWaitingModalState,
  LoadingMethodSelectionState,
} from "./states";
import { shallowCopy } from "utils";
import { CurrentScreenState, IFormInput } from "global-states";
import { ROUTES } from "constants/routes";
import { SIGNIN_FORM, SIGNIN_SCREENS } from "../constants";
import { API_URL } from "constants/api-url";
import GlobalInstanceManager from "instance-manger";
import { CurrentAuthCredState } from "./states";
import { ISignInCredType } from "./types";
import { ENVS } from "constants/common";
import { ERRORS, ErrorTypes } from "constants/errors";

let popupRef: Window | null = null;
/**
 * Custom hook for managing the sign-in process
 */

// eslint-disable-next-line @typescript-eslint/no-empty-function
let circularDependencyMethod: any = () => {};
let isPasskeyRegistrationApproval = false;
export const useSignin = (additionalMethod?: any) => {
  const [loading, setLoading] = useRecoilState(SigninLoadingState);
  // State to manage whether the terms and conditions are checked
  const [checked, setChecked] = useRecoilState(
    IsSignInTermAndConditionEnabledState
  );
  const [loadingMethodSelection, setLoadingMethodSelection] = useRecoilState(LoadingMethodSelectionState);

  const [
    passkeyRegistrationApprovalStatus,
    setIsPasskeyRegistrationApprovalStatus,
  ] = useState("pending");
  const [isVerfiedUser, setIsVerfiedUser] = useRecoilState(IsVerifiedUserState);
  const [showBrowserRegistrationModal, setShowBrowserRegistrationModal] =
    useRecoilState(BrowserRegistrationModalState);
  const [
    showBrowserRegistrationWaitingModal,
    setShowBrowserRegistrationWaitingModal,
  ] = useRecoilState(BrowserRegistrationWaitingModalState);

  if (additionalMethod) {
    circularDependencyMethod = additionalMethod;
  }

  // State to set the current screen
  const setCurrentScreen = useSetRecoilState(CurrentScreenState);

  // Hook to get currentMethod details
  const { currentMethod, env, currentScreen } = useInstance();

  const { get: socialAuth } = useNetwork();

  // State to manage the current authentication method
  const [currentAuthMethod, setCurrentAuthMethod] =
    useRecoilState(CurrentAuthCredState);

  // State to manage the current step in the sign-in process
  const [signInCurrentScreen, setSignInInCurrentScreen] =
    useRecoilState(SignInScreenState);

  // State to manage the form data
  const [formState, setFormState] = useRecoilState(SignInFormState);

  const { get: getWebAuthn, create: createWebAuthn } = useWebAuthn();

  const { post } = useNetwork();
  const { post: loginViaLink } = useNetwork();
  const { post: registerBiometric } = useNetwork();
  const { instanceDetails } = useInstance();

  const [userLoginCredResp, setUserLoginCredResp] = useRecoilState(
    userLoginCredRespState
  );

  const { post: submitCred, loading: submitCredLoading } = useNetwork();

  const { onErrorEvent, onStepChangeEvent, onSuccessEvent, webcomponentProps } =
    GlobalInstanceManager;

  // WebAuthn registration function with auth request source
  const registerWebAuthnWithAuthSource = useCallback(async () => {
    try {
      const resp = await registerBiometric(API_URL.PASSKEY, {
        type: "passkeyRegistrationApproval",
        payload: {
          userId: userLoginCredResp.userId,
        },
      });
      return resp;
    } catch (error) {
      onErrorEvent({
        type: "webAuthn-registration-error",
        error: error,
      });
      return { success: false, error };
    }
  }, [registerBiometric, userLoginCredResp.userId, onErrorEvent]);

  // WebAuthn registration function for browser registration
  const registerWebAuthnForBrowser = useCallback(async () => {
    try {
      // Check if userLoginCredResp is available
      if (!userLoginCredResp || !userLoginCredResp.userId) {
        onErrorEvent({
          type: "webAuthn-registration-error",
          error: "User login credentials not available",
        });
        return {
          success: false,
          error: "User login credentials not available",
        };
      }

      // Get registration options from server
      const resp = await registerBiometric(API_URL.PASSKEY, {
        type: "registrationOpts",
        payload: {
          rpId: window.location.origin,
          rpName: "Domain",
          userId: userLoginCredResp.userId,
        },
        authRequestSource: "login",
      });

      if (resp.statusCode === 200) {
        resp.registrationOptions.authenticatorSelection = {
          residentKey: "preferred",
          userVerification: "preferred",
          requireResidentKey: false,
        };

        onStepChangeEvent({
          ...resp,
          type: "webAuthn-registration",
          from: "browser-registration",
        });

        // Create the WebAuthn credential
        const response: any = await createWebAuthn(resp.registrationOptions);

        if (!response.isError) {
          // Verify the registration
          const verifyResp = await registerBiometric(API_URL.PASSKEY, {
            type: "verifyRegistration",
            payload: {
              registrationOptResponse: {
                ...response,
              },
              userId: userLoginCredResp.userId,
            },
            authRequestSource: "login",
          });

          if (verifyResp.statusCode === 200) {
            onStepChangeEvent({
              type: "webAuthn-registration-success",
              from: "browser-registration",
              message: "Browser WebAuthn registration completed successfully",
            });
            return { success: true, apiResp: resp, webauthnResp: response };
          } else {
            onErrorEvent({
              type: "webAuthn-registration-verify-error",
              ...verifyResp,
            });
            return { success: false, error: verifyResp };
          }
        } else {
          onErrorEvent({
            type: "webAuthn-registration-create-error",
            error: response.error,
          });
          return { success: false, error: response.error };
        }
      } else {
        onErrorEvent({
          type: "webAuthn-registration-options-error",
          ...resp,
        });
        return { success: false, error: resp };
      }
    } catch (error) {
      onErrorEvent({
        type: "webAuthn-registration-error",
        error: error,
      });
      return { success: false, error };
    }
  }, [
    userLoginCredResp,
    registerBiometric,
    onStepChangeEvent,
    createWebAuthn,
    onErrorEvent,
  ]);

  const {
    instance_id,
    verified_user,
    auto_navigate,
    microsoft_redirect_url,
    facebook_redirect_url,
    apple_redirect_url,
    google_redirect_url,
  } = webcomponentProps;

  /**
   * Navigates to the sign-up page
   */
  const moveToSignupPage = useCallback(() => {
    GlobalInstanceManager.onStepChangeEvent({
      type: "change-page",
      pageTo: "signup",
      pageFrom: "singin",
      message: "user moved to signup page",
    });
    setCurrentScreen(ROUTES.SIGNUP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSSO = async (param: any) => {
    if (param.errorCode) {
      onErrorEvent({ ...param, type: "sso-data" });
      if (typeof popupRef?.close === "function") {
        try {
          popupRef.close();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log({ error });
        }

        if (param?.errorCode === "USER_NOT_FOUND" && auto_navigate) {
          circularDependencyMethod({
            email: param?.email ?? "",
            instanceId: instance_id,
          });
          setCurrentScreen(ROUTES.SIGNUP);
        } else {
          onErrorEvent({
            ...ERRORS[param.errorCode as keyof ErrorTypes],
            payload: { email: param?.email ?? "" },
          });
        }
      }
    } else if (Object.keys(param).length > 0 && param.email) {
      onStepChangeEvent({ ...param, type: "sso-data" });
      const currentForm = SIGNIN_FORM.find(
        (el) => el.name === "email"
      ) as IFormInput;
      if (param.type === "signUp") {
        setCurrentScreen(ROUTES.SIGNUP);
        circularDependencyMethod({}, param);
      } else {
        const newObj = shallowCopy(currentForm);
        if (newObj) {
          newObj.value = param.email;
          setFormState([newObj]);
          setSignInInCurrentScreen("methods");
          // sendMessage("login", {
          //   userId: param.userId,
          // });
          setUserLoginCredResp((prev) => ({ ...prev, userId: param.userId }));
        }
      }

      // Close the popup if the close function is available
      if (typeof popupRef?.close === "function") {
        try {
          popupRef.close();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log({ error });
        }
      }
    }
  };

  /**
   * useEffect hook to handle incoming messages and update form state accordingly.
   */
  useEffect(() => {
    // Message event listener function
    const messageListener = (event: MessageEvent) => {
      handleSSO(event.data);
    };

    // Add event listener for messages
    window.addEventListener("message", messageListener);

    // Cleanup function to remove event listener on component unmount
    return () => {
      window.removeEventListener("message", messageListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formState.length === 0 && currentMethod) {
      setCurrentAuthMethod(currentMethod);
      setFormState(SIGNIN_FORM.filter((el) => currentMethod === el.name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMethod]);

  const handleOnApproveStatus = useCallback(
    async (resp: any) => {
      onStepChangeEvent({
        ...resp,
        type: "approval",
        from: "login",
        step: signInCurrentScreen,
      });


      
      if (resp.purpose === "passkeyRegistrationApproval") {
        // eslint-disable-next-line no-console
        setIsPasskeyRegistrationApprovalStatus(resp.status);

        // Check if userLoginCredResp and userId are available
        if (
          userLoginCredResp &&
          userLoginCredResp.userId &&
          resp.status === "APPROVED" &&
          !isPasskeyRegistrationApproval
        ) {
          isPasskeyRegistrationApproval = true;
          await registerWebAuthnForBrowser();
        } else if (resp.status === "REJECTED") {
          setLoadingMethodSelection({
            index: 0,
            loading: false,
          });
        }
        // isPasskeyRegistrationApproval = false;
      } else if (resp.purpose === "emailAndPhoneLoginVerification") {
        setIsPasskeyRegistrationApprovalStatus(resp.status);
      }
    },
    [onStepChangeEvent, registerWebAuthnForBrowser, setLoadingMethodSelection, signInCurrentScreen, userLoginCredResp]
  );

  useEffect(() => {
    if (currentScreen !== "signin") return;

    if (!userLoginCredResp.userId) {
      // eslint-disable-next-line no-console
      console.error("UserId not found");
      return;
    }

    const eventsRef = query(ref(db, userLoginCredResp.userId));

    let isInitialLoad = true;

    const unsubscribe = onChildAdded(eventsRef, (snapshot) => {
      if (isInitialLoad) return; // ignore old events

      const data = snapshot.val();
      const resp = JSON.parse(data.data || "{}");

      if (resp.event === "approvalStatus") {
        handleOnApproveStatus(resp.payload);
      } else if (resp.event === "loginCompleted") {
        onSuccessEvent(resp.payload).finally(() => {
          setLoadingMethodSelection({
            index: 0,
            loading: false,
          });
        });
      }
    });

    // after attaching listener, run once to flip the flag
    get(eventsRef).then(() => {
      isInitialLoad = false;
    });

    return () => unsubscribe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoginCredResp.userId]);

  const handleWebAuthn = useCallback(async () => {
    const resp: any = await post(API_URL.PASSKEY, {
      type: "authenticateOpts",
      payload: {
        rpId: window.location.origin,
        userId: userLoginCredResp.userId,
      },
    });

    if (resp.statusCode !== 200) {
      setShowBrowserRegistrationModal(true);
      return onErrorEvent(resp);
    } else if (resp.authenticateOptions) {
      onStepChangeEvent({
        ...resp,
        type: "webAuthn",
        from: "login",
      });
      const authnResp: any = await getWebAuthn(resp.authenticateOptions);
      if (!authnResp.isError) {
        await post(API_URL.PASSKEY, {
          type: "verifyAuthentication",
          payload: {
            authenticateOptResponse: {
              ...authnResp,
            },
            userId: userLoginCredResp.userId,
          },
        });
      } else {
        // Show browser registration modal when WebAuthn fails (browser not registered for biometric authentication)
        onStepChangeEvent({
          type: "webAuthn-error",
          from: "login",
          error: authnResp.error,
          message: "Browser not registered for biometric authentication",
        });
        setShowBrowserRegistrationModal(true);
      }
    }
  }, [
    post,
    userLoginCredResp.userId,
    onErrorEvent,
    onStepChangeEvent,
    getWebAuthn,
    setShowBrowserRegistrationModal,
  ]);

  /**
   * Placeholder function for submitting credentials
   */
  const onSubmitCred = async (
    authMethod?: "email" | "phone",
    formData?: IFormInput[]
  ) => {
    // Reset token generation flag for new authentication attempt
    GlobalInstanceManager.resetTokenGenerationFlag();
    
    const method = authMethod || currentAuthMethod;
    const {
      countryCode,
      value,
      name = "",
    } = (formData || formState).find((el) => el.name === method) ?? {};
    if (!value || !name) {
      return;
    }

    const payload = {
      instanceId: instance_id,
      [name]:
        method === "phone" ? (value as string)?.replace(/\D/g, "") : value,
      countryCode: countryCode || undefined,
    };

    const resp = await submitCred(API_URL.LOGIN, payload);
    if (resp.statusCode !== 200) {
      onErrorEvent({ ...resp, payload });

      if (
        resp.message === "User not found" &&
        webcomponentProps.auto_navigate
      ) {
        await circularDependencyMethod?.(payload);
        moveToSignupPage();
      }
    } else {
      onStepChangeEvent({ ...resp, type: "signin-form" });
      // sendMessage("login", {
      //   userId: resp.userId,
      // });
      setUserLoginCredResp(resp);
      setSignInInCurrentScreen(SIGNIN_SCREENS.METHODS);
    }
    return { ...resp, payload };
  };

  /**
   * Placeholder function for submitting sign-in form
   */
  const onSubmitSignin = useCallback(() => {
    return "onSubmitSignin";
  }, []);

  /**
   * Handles change in form input fields
   * @param {ChangeEvent<HTMLInputElement>} event - The input change event
   */
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target ?? {};
      const { index, validation, formatter } = event as any;
      let inputVal = "";
      let validationInput = "";
      if (formatter) {
        const newData = formatter({
          value,
          countryCode: formState[index]?.countryCode,
        });
        inputVal = newData.formatted ?? value;
        validationInput = newData.validationInput ?? value;
      } else {
        inputVal = value;
        validationInput = value;
      }

      setFormState(() => {
        const newObj = shallowCopy(formState) ?? formState;
        if (newObj[index]) {
          newObj[index]["value"] = inputVal;
          if (validation) {
            const { isError, errorMessage } = validation(validationInput);
            newObj[index]["isError"] = isError;
            newObj[index]["errorMessage"] = errorMessage;
          }
        }

        return newObj;
      });
    },
    [formState, setFormState]
  );

  /**
   * Handles change in country code for phone number inputs
   * @param {string} value - The new country code
   * @param {number} index - The index of the form field to update
   */
  const handleChangeCountry = useCallback(
    (value: string, index: number) => {
      setFormState(() => {
        const newObj = shallowCopy(formState) ?? formState;
        if (newObj[index]) {
          newObj[index].countryCode = value;
        }

        return newObj;
      });
    },
    [formState, setFormState]
  );

  /**
   * Handles changing the authentication method
   * @param {string} key - The new authentication method key
   */
  const handleOnChangeMethod = (key: ISignInCredType) => {
    setCurrentAuthMethod(key);
    setFormState(SIGNIN_FORM.filter((el) => el.name === key));
  };

  const handleLoginViaLink = useCallback(async () => {
    const isEmailAndPhone =
      instanceDetails.signUp?.scope.includes("email") &&
      instanceDetails.signUp?.scope.includes("phone");
    const purpose = isEmailAndPhone
      ? "emailAndPhoneLoginVerification"
      : currentAuthMethod === "email"
      ? "emailLoginVerification"
      : "phoneLoginVerification";
    const resp = await loginViaLink(API_URL.APPROVAL, {
      purpose,
      userId: userLoginCredResp.userId,
    });

    if (resp.statusCode !== 200) {
      onErrorEvent({
        type: "resend",
        ...resp,
      });
    } else {
      onStepChangeEvent({
        data: {
          ...resp,
        },
        type: "resend",
      });
    }
  }, [
    instanceDetails.signUp?.scope,
    currentAuthMethod,
    loginViaLink,
    userLoginCredResp.userId,
    onErrorEvent,
    onStepChangeEvent,
  ]);

  /**
   * Handles option selection in the sign-in methods step
   * @param {any} e - The selected option
   */
  const handleAuthMethodOptions = useCallback(
    async (e: any) => {
      if (e.value === "biometric") {
        await handleWebAuthn();
      } else if (e.value === "otp") {
        setSignInInCurrentScreen(SIGNIN_SCREENS.APPROVAL);
        await handleLoginViaLink();
      } else {
        await handleLoginViaLink();
      }
    },
    [handleLoginViaLink, handleWebAuthn, setSignInInCurrentScreen]
  );

  const handleSocialSignUp = useCallback(
    async (el: { key: string }) => {
      // Reset token generation flag for new authentication attempt
      GlobalInstanceManager.resetTokenGenerationFlag();
      
      const authType = auto_navigate ? "embeded" : "login";

      setLoading(true);
      let url = `${API_URL.OAUTH}/${
        el.key
      }?instanceId=${instance_id}&type=${authType}${
        env === ENVS.DEV ? "&test=true" : ""
      }&provider=${el.key}`;
      if (el.key === "facebook" && facebook_redirect_url) {
        url = url + `&redirect_url=${facebook_redirect_url}`;
      }
      if (el.key === "microsoft" && microsoft_redirect_url) {
        url = url + `&redirect_url=${microsoft_redirect_url}`;
      }

      if (el.key === "google" && google_redirect_url) {
        url = url + `&redirect_url=${google_redirect_url}`;
      }
      if (el.key === "apple" && apple_redirect_url) {
        url = url + `&redirect_url=${apple_redirect_url}`;
      }
      if (webcomponentProps.platform) {
        url = url + `&platform=${webcomponentProps.platform}`;
      }
      const resp = await socialAuth(url);
      if (resp?.statusCode === 200 && resp?.data?.url) {
        onStepChangeEvent({
          ...resp.data,
          type: "sso",
          from: "login",
          key: el.key,
        });
        if (!/ios|android/.test(webcomponentProps.platform)) {
          popupRef = window.open(
            resp.data.url,
            "popup",
            "width=600,height=400"
          );
        }
      }
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      env,
      instance_id,
      socialAuth,
      microsoft_redirect_url,
      facebook_redirect_url,
      apple_redirect_url,
      google_redirect_url,
    ]
  );

  const handleCombinedInputChange = useCallback(
    (form: any) => {
      if (form.name === "phone") {
        setCurrentAuthMethod("phone");
      } else {
        setCurrentAuthMethod("email");
      }
      const index = SIGNIN_FORM.findIndex((el) => el.name === form.name);
      setFormState(() => {
        const newObj = shallowCopy(SIGNIN_FORM) ?? SIGNIN_FORM;
        if (newObj[index]) {
          newObj[index]["value"] = form.value;
          newObj[index]["isHidden"] = false;
          newObj[index]["isError"] = form.isError;
          if (form.name === "phone") {
            newObj[index]["countryCode"] = form.countryCode;
          }
        }
        return newObj;
      });
    },
    [setCurrentAuthMethod, setFormState]
  );

  const exchangeWebCode = async (event: {
    code?: string;
    access_token?: string;
    state: any;
  }) => {
    const { code, access_token, state } = event;
    const params = new URLSearchParams();
    if (JSON.parse(state).provider)
      params.append("provider", JSON.parse(state).provider);
    //params.append('provider', 'facebook')
    if (code) params.append("code", code);
    if (access_token) params.append("access_token", access_token);
    if (state) params.append("state", state);
    const url = `${
      GlobalInstanceManager.webcomponentProps.apiUrl
    }/oauth?${params.toString()}`;
    window.location.href = url;
  };

  const manageDefaultUser = useCallback(async (user: any) => {
    try {
      if (Object.keys(user).length === 0 || !webcomponentProps.auto_navigate) {
        return;
      }

      setCurrentScreen("signin");
      const authMethod = user.email ? "email" : "phone";
      setCurrentAuthMethod(authMethod);
      const newObj = shallowCopy(SIGNIN_FORM);
      const payload = newObj
        .map((el: any) => {
          return {
            ...el,
            countryCode: user.countryCode || undefined,
            value: user[el.name] || "",
          };
        })
        .filter((el: any) => el.name === authMethod);

      setFormState(payload);
      await onSubmitCred(authMethod, payload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("ERROR:::", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOtherAccount = () => {
    setCurrentAuthMethod(currentMethod);
    setFormState(SIGNIN_FORM.filter((el) => currentMethod === el.name));
    setSignInInCurrentScreen(SIGNIN_SCREENS.SIGNIN);
    setIsVerfiedUser(false);
  };

  const handleVerifiedUser = async () => {
    setLoading(true);
    setSignInInCurrentScreen(SIGNIN_SCREENS.LOADING);
    const isEmailVerified = verified_user.email;
    const isPhoneVerified = verified_user.phone;
    if (isEmailVerified || isPhoneVerified) {
      const field: "email" | "phone" = isEmailVerified ? "email" : "phone";
      const value = isEmailVerified ? verified_user.email : verified_user.phone;

      const data = SIGNIN_FORM.filter((el) => el.name === field).map((el) => ({
        ...el,
        value,
        ...(field === "phone" && {
          countryCode: verified_user.countryCode ?? "+1",
        }),
      }));
      setCurrentAuthMethod(field);
      setFormState(data);
      const payload: {
        instanceId: string;
        phone?: string;
        email?: string;
        countryCode?: string;
      } = {
        instanceId: instance_id as string,
        [field]:
          field === "phone" ? (value as string)?.replace(/\D/g, "") : value,
        countryCode:
          field === "phone" ? verified_user.countryCode ?? "+1" : undefined,
      };

      const method = payload.email ? "email" : "phone";

      const payloadData = {
        ...payload,
        name: method,
        value: payload.email || payload.phone,
      };
      const resp = await onSubmitCred(method, [payloadData as any]);
      if (resp?.requestId && resp?.userId) {
        setIsVerfiedUser(true);
      } else {
        handleOtherAccount();
      }
      setLoading(false);
    }
  };
  /**
   * Appends object keys to the URL as query params and makes a GET request.
   *
   * @param {string} baseUrl - The base URL to send the GET request to.
   * @param {Object} params - Key-value pairs to append as query parameters.
   * @returns {Promise<any>} - The JSON response from the server.
   */
  async function exchangeCode(params: any) {
    const updatedParam = params || {};
    updatedParam.instanceId = updatedParam.instance_id || instance_id;
    const url = `/oauth?provider=google&access_token=${updatedParam.access_token}&code=${updatedParam.code}&redirect_url=com.googleusercontent.apps.718985979321-klp4ukpg1lch0m4acd4pe3dkmekr30hh%3A%2Foauth2redirect&instanceId=${instance_id}&type=embeded&responseType=json`;
    try {
      const response = await socialAuth(url);
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("exchangeToken failed:", error);
      throw error;
    }
  }

  const handleApprovalBackToMethod = () => {
    isPasskeyRegistrationApproval = false;
    setSignInInCurrentScreen(SIGNIN_SCREENS.METHODS);
    setLoadingMethodSelection({
      index: 0,
      loading: false,
    });
    setTimeout(() => {
      setIsPasskeyRegistrationApprovalStatus("pending");
    }, 1000);
  };

  const handleApprovalBackToSigninPage = () => {
    isPasskeyRegistrationApproval = false;
    setLoadingMethodSelection({
      index: 0,
      loading: false,
    });
    setSignInInCurrentScreen(SIGNIN_SCREENS.SIGNIN);
  };

  return {
    onSubmitCred,
    onSubmitSignin,
    form: formState,
    checked,
    handleChangeCountry,
    handleChange,
    handleOnChangeMethod,
    moveToSignupPage,
    currentAuthMethod,
    setChecked,
    handleWebAuthn,
    handleLoginViaLink,
    handleAuthMethodOptions,
    signInCurrentScreen,
    setSignInInCurrentScreen,
    handleSocialSignUp,
    submitCredLoading,
    handleCombinedInputChange,
    manageDefaultUser,
    loading,
    handleOtherAccount,
    verified_user,
    isVerfiedUser,
    handleVerifiedUser,
    handleSignInSSO: handleSSO,
    exchangeCode,
    exchangeWebCode,
    showBrowserRegistrationModal,
    setShowBrowserRegistrationModal,
    showBrowserRegistrationWaitingModal,
    setShowBrowserRegistrationWaitingModal,
    handleBrowserRegistrationResend: () => {
      // Hardcoded resend logic for browser registration
      onStepChangeEvent({
        type: "browser-registration-resend",
        from: "browser-registration-waiting",
        message: "Browser registration request resent",
      });
    },
    registerWebAuthnForBrowser,
    registerWebAuthnWithAuthSource,
    passkeyRegistrationApprovalStatus,
    handleApprovalBackToMethod,
    handleApprovalBackToSigninPage,
    setLoadingMethodSelection,
    loadingMethodSelection,
  };
};
